import React, { useMemo, useState } from "react";
import { Button, Chip, ProgressBar, ProgressSpinner, Tooltip } from "@allygpo/ally-ux";
import { Card } from "../../components/ui";
import { ProductFamily } from "../../types";
import "./QtdDistributionChart.css";

interface QtdDistributionChartProps {
  data: ProductFamily[];
  loading?: boolean; // show a spinner instead of the empty-state while fetching
}

// One bar per measure, per family. Colour identifies the measure, so it's greys: blue means a product family elsewhere.
const MEASURES = [
  { key: "cu", label: "Category units", color: "var(--a-p-color-gray-700)" },
  { key: "spend", label: "Spend", color: "var(--a-p-color-gray-500)" },
  { key: "iu", label: "Invoice units", color: "var(--a-p-color-gray-300)" },
] as const;

type MeasureKey = typeof MEASURES[number]["key"];

const toPercent = (ratio: number | undefined): number => (ratio ?? 0) * 100;

const formatPct = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
};

interface FamilyPct {
  pf: ProductFamily;
  cu: number;
  spend: number;
  iu: number;
}

/** Quarter-to-date share of each measure by product family, largest category-unit share first. */
const QtdDistributionChart: React.FC<QtdDistributionChartProps> = ({ data, loading = false }) => {
  const [showNoQtd, setShowNoQtd] = useState(false);

  const { purchased, noQtd } = useMemo(() => {
    const withPct: FamilyPct[] = data.map((pf) => ({
      pf,
      cu: toPercent(pf.product_family_to_category_qtd_ratio),
      spend: toPercent(pf.product_family_to_category_spend_qtd_ratio),
      iu: toPercent(pf.product_family_to_category_invoice_units_qtd_ratio),
    }));

    const isPurchased = (x: FamilyPct) => x.cu > 0 || x.spend > 0 || x.iu > 0;
    const purchasedList = withPct.filter(isPurchased).sort((a, b) => b.cu - a.cu);
    const noQtdList = withPct.filter((x) => !isPurchased(x));

    return { purchased: purchasedList, noQtd: noQtdList };
  }, [data]);

  // Axis runs to the next 25% above the largest share, so the bars use the width.
  const axisMax = useMemo(() => {
    const top = purchased.reduce((m, x) => Math.max(m, x.cu, x.spend, x.iu), 0);
    return Math.min(100, Math.max(25, Math.ceil(top / 25) * 25));
  }, [purchased]);
  const ticks = Array.from({ length: axisMax / 25 + 1 }, (_, i) => i * 25);

  const top = purchased[0];
  const subtitle =
    data.length === 0
      ? undefined
      : `${purchased.length} of ${data.length} families purchased${top ? ` · most category units: ${top.pf.product_family_name} (${formatPct(top.cu)})` : ''}`;

  const legend = (
    <div className="flex u-gap-4 flex-wrap text-xs text-secondary">
      {MEASURES.map((m) => <span key={m.key} className="inline-flex align-items-center u-gap-1 white-space-nowrap"><i className="siq-swatch" style={{ background: m.color }} />{m.label}</span>)}
    </div>
  );

  return (
    <Card title="Quarter to date distribution" subtitle={subtitle} actions={!loading && purchased.length > 0 ? legend : undefined} className="qtd-card" collapsible defaultCollapsed>
      {loading ? (
        <div className="qtd-state flex flex-column align-items-center justify-content-center u-gap-3 u-p-4 text-xs text-center min-h-11rem text-selected font-semibold">
          <ProgressSpinner style={{ width: 44, height: 44 }} strokeWidth="4" fill="transparent" animationDuration="1.4s" />
          <span>Loading quarter to date distribution…</span>
        </div>
      ) : data.length === 0 ? (
        <div className="qtd-state flex flex-column align-items-center justify-content-center u-gap-3 u-p-4 text-xs text-tertiary text-center min-h-9rem">No quarter to date distribution available for this category.</div>
      ) : purchased.length === 0 ? (
        <div className="qtd-state flex flex-column align-items-center justify-content-center u-gap-3 u-p-4 text-xs text-tertiary text-center min-h-9rem">No families purchased this quarter.</div>
      ) : (
        <div className="qtd-chart flex flex-column u-gap-3 u-pt-4 u-px-4 u-pb-3" role="table" aria-label="Quarter to date distribution">
          {/* Hover label per bar, as the chart's tooltip gave before. */}
          <Tooltip target=".qtd-bar-line" position="top" mouseTrack mouseTrackTop={12} />
          {purchased.map((x) => (
            <div key={x.pf.product_family_id} className="qtd-family" role="row">
              <span className="text-xs font-medium min-w-0" role="rowheader">{x.pf.product_family_name}</span>
              <div className="flex flex-column u-gap-2px min-w-0">
                {MEASURES.map((m) => {
                  const value = x[m.key as MeasureKey];
                  return (
                    // Ally ProgressBar per measure; the label's slot (2rem + 12px gap) matches the axis's right margin.
                    <div key={m.key} className="qtd-bar-line flex align-items-center u-gap-3" role="cell" data-pr-tooltip={`${x.pf.product_family_name} · ${m.label}: ${formatPct(value)}`}>
                      {/* ProgressBar sizes its own wrapper, so this div is what grows. No track: bars only, like a chart. */}
                      <div className="flex-1 min-w-0"><ProgressBar value={(value / axisMax) * 100} color={m.color} className="w-full" style={{ background: 'transparent' }} aria-hidden="true" /></div>
                      <span className="qtd-bar-value w-2rem flex-none text-2xs text-secondary white-space-nowrap">{formatPct(value)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="qtd-family align-items-start" aria-hidden="true">
            <span />
            <div className="qtd-axis">
              {ticks.map((t) => <span key={t} style={{ left: `${(t / axisMax) * 100}%` }}>{t}%</span>)}
            </div>
          </div>
        </div>
      )}

      {noQtd.length > 0 && (
        <div className="flex align-items-center u-gap-3 flex-wrap border-t-1 border-default u-py-1 u-px-3">
          <Button variant="secondary-text" onClick={() => setShowNoQtd((s) => !s)} aria-expanded={showNoQtd}>
            <i className={`pi ${showNoQtd ? "pi-chevron-down" : "pi-chevron-right"}`} aria-hidden="true" />
            <span><b>{noQtd.length} famil{noQtd.length === 1 ? "y" : "ies"}</b> with no QTD purchases</span>
          </Button>
          {showNoQtd && (
            <div className="flex u-gap-2 flex-wrap u-pb-2">
              {noQtd.map((x) => <Chip key={x.pf.product_family_id} label={x.pf.product_family_name} />)}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default QtdDistributionChart;
